import { createAsyncThunk } from "@reduxjs/toolkit";
import { usersApi } from "../api/users.api";
import type { InviteUserRequest, UpdateUserRequest } from "../domain/user.types";
import {
  userAdded,
  userUpdated,
  usersLoaded,
  usersRequestFailed,
  usersRequestStarted,
  type UsersState,
} from "./users.slice";

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export const fetchUsers = createAsyncThunk<void, string | void, { state: { users: UsersState } }>(
  "users/fetchUsers",
  async (search, { dispatch }) => {
    dispatch(usersRequestStarted(search || undefined));
    try {
      const users = await usersApi.list(search ?? undefined);
      dispatch(usersLoaded(users));
    } catch (err) {
      dispatch(usersRequestFailed(errorMessage(err)));
      throw err;
    }
  },
  {
    // A search is always a deliberate, fresh query - never cached. The
    // unfiltered mount-time call (UsersListPage, AuditLogListPage's user
    // picker) is cached, but only once we know the cached data is itself
    // the unfiltered list - otherwise a page that wants the full list right
    // after another page searched would wrongly get served that subset.
    condition: (search, { getState }) => {
      if (search) return true;
      const { status, lastSearch } = getState().users;
      if (status === "loading") return false;
      return !(status === "succeeded" && lastSearch === null);
    },
  },
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, payload }: { id: string; payload: UpdateUserRequest }, { dispatch }) => {
    const user = await usersApi.update(id, payload);
    dispatch(userUpdated(user));
    return user;
  },
);

export const inviteUser = createAsyncThunk(
  "users/inviteUser",
  async (payload: InviteUserRequest, { dispatch }) => {
    const user = await usersApi.invite(payload);
    dispatch(userAdded(user));
    return user;
  },
);
